import { useEffect, useRef, useState, useCallback } from "react";

// Collision-safe placement for the floating Ask Eli button and its panel.
// Priority: (1) never overlap protected content ([data-eli-avoid] regions + interactive controls),
// (2) stay inside the viewport, (3) honour the user's saved position when it is safe,
// (4) otherwise take the nearest safe candidate from an edge grid. Re-evaluated on route, resize, scroll,
// DOM changes (debounced) and a slow heartbeat; hidden under dialogs.
const SIZE = { full: { w: 128, h: 44 }, compact: { w: 48, h: 48 } };
const PANEL = { w: 380, h: 640 };
const MARGIN = 12;
const DEBOUNCE_MS = 250, HEARTBEAT_MS = 10000;   // was 120 ms / 2.5 s: heartbeat is a safety net, the observer does the real work
export const STATS = { computes: 0, fastPath: 0, mutations: 0, scheduled: 0, ms: 0 };   // read-only perf counters (window.__eliAnchorStats)
if (typeof window !== "undefined") window.__eliAnchorStats = STATS;
const INTERACTIVE = "button, a[href], input, select, textarea, [role=button], [data-eli-avoid], [contenteditable=true], aside, nav, header";
const CRITICAL = "input, textarea, select, [data-eli-avoid], [data-testid*=checkout], [data-testid*=pay], [data-testid*=keypad], [data-testid*=hold-sale], [data-testid=select-customer], [data-testid*=cart-], nav, header, aside";

export const rectFor = (pos, size) => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const x0 = pos.left != null ? pos.left : vw - pos.right - size.w;
  return { x0, y0: vh - pos.bottom - size.h, x1: x0 + size.w, y1: vh - pos.bottom };
};
const inViewport = (r) => r.x0 >= 0 && r.y0 >= 0 && r.x1 <= window.innerWidth && r.y1 <= window.innerHeight;
const hits = (r, selector) => {
  const pts = [[r.x0 + 4, r.y0 + 4], [r.x1 - 4, r.y0 + 4], [r.x0 + 4, r.y1 - 4], [r.x1 - 4, r.y1 - 4], [(r.x0 + r.x1) / 2, (r.y0 + r.y1) / 2]];
  return pts.some(([x, y]) => document.elementsFromPoint(x, y).some((el) => !el.closest("[data-eli-root]") && (el.matches(selector) || el.closest(selector))));
};
const overlapsAvoid = (r) => [...document.querySelectorAll("[data-eli-avoid]")].some((el) => {
  const b = el.getBoundingClientRect();
  return b.width && b.height && !(b.right < r.x0 || b.left > r.x1 || b.bottom < r.y0 || b.top > r.y1);
});
// Safe = inside viewport, no rect overlap with any protected region, and no interactive control under the footprint.
export const isSafe = (pos, size) => { const r = rectFor(pos, size); return inViewport(r) && !overlapsAvoid(r) && !hits(r, INTERACTIVE); };
const isTolerable = (pos, size) => { const r = rectFor(pos, size); return inViewport(r) && !overlapsAvoid(r) && !hits(r, CRITICAL); };

// Candidate grid along the right edge, left edge (past the sidebar) and bottom edge, nearest-to-preferred first.
const candidates = (size, preferred) => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const aside = document.querySelector("aside")?.getBoundingClientRect();
  const leftEdge = aside && aside.width > 0 && aside.left === 0 ? Math.round(aside.right) + MARGIN : MARGIN;
  const out = [];
  for (let b = MARGIN; b + size.h < vh - MARGIN; b += 72) { out.push({ bottom: b, right: MARGIN }); out.push({ bottom: b, left: leftEdge }); }
  for (let x = leftEdge + 160; x + size.w < vw - MARGIN - 160; x += 160) out.push({ bottom: MARGIN, left: x });
  const ref = preferred ? rectFor(preferred, size) : rectFor({ bottom: 20, right: 20 }, size);
  const d = (p) => { const r = rectFor(p, size); return Math.hypot(r.x0 - ref.x0, r.y0 - ref.y0); };
  return out.sort((a, b) => d(a) - d(b));
};

export const snapToEdge = (pos, size) => {
  // Snap a free-drag position to the nearest horizontal edge (left/right) while keeping its vertical offset.
  const r = rectFor(pos, size); const vw = window.innerWidth, vh = window.innerHeight;
  const bottom = Math.max(MARGIN, Math.min(vh - size.h - MARGIN, Math.round(vh - r.y1)));
  const aside = document.querySelector("aside")?.getBoundingClientRect();
  const leftEdge = aside && aside.width > 0 && aside.left === 0 ? Math.round(aside.right) + MARGIN : MARGIN;
  const toRight = (vw - r.x1) <= (r.x0 - leftEdge);
  return toRight ? { bottom, right: MARGIN } : { bottom, left: leftEdge };
};

export function useSafeAnchor(pathname, preferred = null) {
  const [state, setState] = useState({ anchor: { bottom: 20, right: 20 }, compact: false, hidden: false, panelSide: "right", panelLeft: 20, panelWidth: PANEL.w, usingPreferred: false });
  const last = useRef(state);
  // Only commit a state change when the result actually differs: otherwise every heartbeat re-rendered Ask Eli,
  // whose style mutation re-triggered the MutationObserver (compute → render → observe → compute).
  const commit = (next) => {
    const p = last.current;
    if (p.hidden === next.hidden && p.compact === next.compact && p.panelSide === next.panelSide && p.panelLeft === next.panelLeft && p.panelWidth === next.panelWidth &&
        p.usingPreferred === next.usingPreferred && p.anchor.bottom === next.anchor.bottom && p.anchor.left === next.anchor.left && p.anchor.right === next.anchor.right) return;
    last.current = next; setState(next);
  };
  const compute = useCallback(() => {
    const t0 = performance.now();
    STATS.computes++;
    const narrow = window.innerWidth < 640;
    const dialogOpen = !!document.querySelector("[role=dialog][data-state=open], [role=alertdialog][data-state=open], [data-testid=lock-overlay]");
    if (dialogOpen) { commit({ ...last.current, hidden: true }); STATS.ms += performance.now() - t0; return; }
    const size = narrow ? SIZE.compact : SIZE.full;
    let chosen = null, usingPreferred = false;
    if (preferred && isSafe(preferred, size)) { chosen = preferred; usingPreferred = true; }
    // Fast path: the anchor we already use is still safe → keep it (one isSafe instead of a full candidate scan).
    const cur = last.current;
    if (!chosen && !cur.hidden && !cur.compact && cur.anchor && isSafe(cur.anchor, size)) { chosen = cur.anchor; usingPreferred = cur.usingPreferred; STATS.fastPath++; }
    const list = chosen ? [] : candidates(size, preferred);
    let pick = chosen || list.find((a) => isSafe(a, size));
    const fallback = pick || list.find((a) => isTolerable(a, size)) || { bottom: 20, right: 20 };
    chosen = pick || fallback;
    let panelSide = chosen?.left != null ? "left" : "right";
    const aside = document.querySelector("aside")?.getBoundingClientRect();
    const panelLeft = aside && aside.width > 0 && aside.left === 0 ? Math.round(aside.right) + 16 : 20;
    let panelWidth = PANEL.w;
    if (!narrow) {
      const ph = Math.min(PANEL.h, window.innerHeight - 80);
      if (overlapsAvoid(rectFor(panelSide === "left" ? { bottom: 20, left: panelLeft } : { bottom: 20, right: 20 }, { w: PANEL.w, h: ph }))) panelSide = panelSide === "left" ? "right" : "left";
      if (panelSide === "left") {
        const minLeft = Math.min(...[...document.querySelectorAll("[data-eli-avoid]")].map((el) => el.getBoundingClientRect().left).filter((x) => x > panelLeft), Infinity);
        if (Number.isFinite(minLeft)) panelWidth = Math.max(280, Math.min(PANEL.w, minLeft - panelLeft - 12));
      }
    }
    commit({ anchor: chosen, compact: narrow || !pick, hidden: false, panelSide, panelLeft, panelWidth, usingPreferred });
    STATS.ms += performance.now() - t0;
  }, [preferred]);

  useEffect(() => {
    let t; const run = () => { clearTimeout(t); t = setTimeout(compute, DEBOUNCE_MS); };
    run();
    const hb = setInterval(() => { if (!document.hidden) compute(); }, HEARTBEAT_MS);
    window.addEventListener("resize", run); window.addEventListener("orientationchange", run); window.addEventListener("scroll", run, { capture: true, passive: true });
    // Ignore mutations inside Ask Eli itself (its own renders) and plain text/attribute churn from cart updates: only
    // structural changes or data-state flips (dialogs/popovers) elsewhere in the page can change the safe area.
    const mo = new MutationObserver((muts) => {
      STATS.mutations += muts.length;
      for (const m of muts) {
        if (m.target?.closest?.("[data-eli-root]")) continue;
        if (m.type === "attributes" && m.attributeName !== "data-state") continue;
        STATS.scheduled++; run(); return;
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => { clearTimeout(t); clearInterval(hb); window.removeEventListener("resize", run); window.removeEventListener("orientationchange", run); window.removeEventListener("scroll", run, { capture: true }); mo.disconnect(); };
  }, [compute, pathname]);
  return state;
}
